// KPI dashboard home (/kpi/nba) and one player's dashboard (/kpi/nba/player/:id). The design's dashboard note: the
// page structure does not change; one window selector sits above it and the Tonight block becomes the selected
// window. Trevor's review (2026-10-02): Last 5 / 10 / 20 always run through the latest game; Custom is any set of
// games picked from a list; a multi-game window swaps the postgame header for its name, dates and record; a
// Full screen button replaces Print. State lives in the URL so a view can be shared.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  FullScreenButton,
  Pending,
  useKpiData,
  useKpiReady,
  WindowHead,
} from "./common";
import { asWindow, windowTitle } from "./kpiLib";
import GamePicker, {
  PickerGame,
  PickerPlayer,
} from "../../Elements/GamePicker/GamePicker";
import { POR_TEAM_ID } from "../../../TeamReport/constants";
import PacketView, { PACKET_REPORTS, PacketReport } from "./PacketView";
import PlayerKpiPage from "./PlayerKpiPage";
import PlayerPage from "./PlayerPage";
import TeamKpiPage from "./TeamKpiPage";
import type {
  KpiGameListItem,
  KpiResponse,
  KpiSeasonsResponse,
} from "./types";

const BASE = "/kpi/nba";
const seasonLabel = (s: number) => `${s}-${String(s + 1).slice(-2)}`;
const WINDOWS: { key: string; label: string }[] = [
  { key: "game", label: "Game" },
  { key: "last5", label: "Last 5" },
  { key: "last10", label: "Last 10" },
  { key: "last20", label: "Last 20" },
  { key: "custom", label: "Custom" },
  { key: "season", label: "Season" },
];

function windowQuery(w: string, picked: string[]) {
  if (w.startsWith("last")) return `window=last&n=${w.slice(4)}`;
  if (w === "custom")
    return `window=games&games=${picked.map(encodeURIComponent).join(",")}`;
  return `window=${w}`;
}

export default function KpiHome({ single = false }: { single?: boolean }) {
  const { playerId: rawPlayer = "" } = useParams();
  const playerId = decodeURIComponent(rawPlayer);
  const [params, setParams] = useSearchParams();
  const view = single ? "player" : params.get("view") || "team";
  const w = params.get("w") || "game";
  const season = params.get("season") || "";
  const game = params.get("game") || "";
  const glgame = params.get("glgame") || "";
  const urlPicked = useMemo(
    () => (params.get("games") || "").split(",").filter(Boolean),
    [params],
  );
  const set = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) =>
        v ? next.set(k, v) : next.delete(k),
      );
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // The custom pick updates the page after a short pause, not on every click of a run.
  const [picked, setPicked] = useState<string[]>(urlPicked);
  useEffect(() => setPicked(urlPicked), [urlPicked]);
  useEffect(() => {
    if (picked.join(",") === urlPicked.join(",")) return;
    const t = setTimeout(() => set({ games: picked.join(",") }), 600);
    return () => clearTimeout(t);
  }, [picked, urlPicked, set]);

  const games = useKpiData<{
    season: number;
    games: KpiGameListItem[];
    gleague?: KpiGameListItem[];
  }>(
    `api/kpi/games?${season ? `season=${season}&` : ""}${single ? `player=${encodeURIComponent(playerId)}` : ""}`,
  );
  const seasonNum = season || (games.data ? String(games.data.season) : "");
  // The seasons with KPI values (Trevor 2026-10-07): a past season is measured against this season's list and goals.
  const seasons = useKpiData<KpiSeasonsResponse>("api/kpi/seasons").data;
  const regular = useMemo(
    () => (games.data?.games || []).filter((g) => g.seasonType === "regular"),
    [games.data],
  );
  // Who played in each game, for the picker's played / didn't play filter: the report picker's list for POR.
  const roster = useKpiData<{ games: PickerGame[]; players: PickerPlayer[] }>(
    w === "custom" && seasonNum
      ? `api/teams/${POR_TEAM_ID}/reports/custom/games?season=${seasonNum}`
      : null,
  ).data;
  const pickerGames = useMemo(() => {
    const played = new Map((roster?.games || []).map((g) => [g.gameId, g.played]));
    return regular.map((g) => ({ ...g, played: played.get(g.gameId) }));
  }, [regular, roster]);
  const kind =
    view === "players" ? "players" : view === "player" ? "player" : "team";
  const packet = view === "packet";
  const packetReport = (params.get("report") || "ownership") as PacketReport;
  const customEmpty = w === "custom" && !urlPicked.length;
  // the window's query, shared by every view's requests (the packet view makes one per part)
  const query =
    seasonNum && !customEmpty
      ? `season=${seasonNum}&${windowQuery(w, urlPicked)}` +
        (w === "game" && game ? `&through=${encodeURIComponent(game)}` : "")
      : null;
  const path =
    query && !packet
      ? `api/kpi/dashboard/${kind}?${query}` +
        (w === "game" && glgame && single
          ? `&glgame=${encodeURIComponent(glgame)}`
          : "") +
        (single ? `&player=${encodeURIComponent(playerId)}` : "")
      : null;
  // the packet view's header (window name, dates, record) comes from its own team request; the dashboard's from this
  const packetHead = useKpiData<KpiResponse>(packet && query ? `api/kpi/dashboard/team?${query}` : null);
  const load = useKpiData<KpiResponse>(path);
  const root = useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = useState(true);
  const onSettled = useCallback((s: boolean) => setFitted(s), []);
  const ready = useKpiReady(root, packet ? packetHead : load, kind === "players" && !fitted);
  const data = useMemo(
    () => (load.data && w !== "game" ? asWindow(load.data) : load.data),
    [load.data, w],
  );
  const headData = packet ? packetHead.data : data;
  const wt =
    headData && w !== "game"
      ? windowTitle(headData.meta.window?.selected, headData.meta.windowSummary)
      : null;
  const win: WindowHead | null = wt
    ? { ...wt, summary: headData?.meta.windowSummary }
    : null;
  const winLabel = WINDOWS.find((x) => x.key === w)?.label || "Game";
  const tg = headData?.meta.targetsSeason;
  const goalsLabel =
    tg && headData?.meta.season && tg !== headData.meta.season
      ? `${seasonLabel(tg)} goals`
      : undefined;
  const gameId = headData?.meta.game?.id;
  const twoWayGames = single ? games.data?.gleague || [] : [];
  const playerLink = (pid: string) =>
    `${BASE}/player/${encodeURIComponent(pid)}?w=${w}` +
    (w === "game" && gameId ? `&game=${encodeURIComponent(gameId)}` : "") +
    (w === "custom" && urlPicked.length
      ? `&games=${urlPicked.join(",")}`
      : "") +
    (season ? `&season=${season}` : "");

  return (
    <div className="kpi-doc" ref={root} {...ready}>
      <div className="kpi-webbar">
        {single ? (
          <Link
            to={`${BASE}?view=players&w=${w}${w === "game" && game ? `&game=${game}` : ""}${w === "custom" && urlPicked.length ? `&games=${urlPicked.join(",")}` : ""}${season ? `&season=${season}` : ""}`}
            style={{ fontWeight: 700 }}
          >
            ← Player KPIs
          </Link>
        ) : (
          <span className="kpi-seg">
            {[
              ["team", "Team"],
              ["players", "Players"],
              ["packet", "Packet"],
            ].map(([k, l]) => (
              <button
                key={k}
                className={view === k ? "on" : ""}
                onClick={() => set({ view: k })}
              >
                {l}
              </button>
            ))}
          </span>
        )}
        {(seasons?.seasons.length || 0) > 1 && (
          <>
            <span className="lbl">Season</span>
            <span className="kpi-seg">
              {seasons!.seasons.map((x) => (
                <button
                  key={x.season}
                  className={String(x.season) === seasonNum ? "on" : ""}
                  onClick={() =>
                    set({
                      season: String(x.season),
                      game: "",
                      games: "",
                      glgame: "",
                    })
                  }
                >
                  {x.label}
                </button>
              ))}
            </span>
          </>
        )}
        <span className="lbl">Window</span>
        <span className="kpi-seg">
          {WINDOWS.map((x) => (
            <button
              key={x.key}
              className={w === x.key ? "on" : ""}
              onClick={() => set({ w: x.key })}
            >
              {x.label}
            </button>
          ))}
        </span>
        {w === "game" && twoWayGames.length > 0 && (
          // A two-way's single game comes from one of two lists: his NBA games or his G League games.
          <span className="kpi-seg">
            <button
              className={!glgame ? "on" : ""}
              onClick={() => set({ glgame: "", game: game || "" })}
            >
              NBA
            </button>
            <button
              className={glgame ? "on" : ""}
              onClick={() =>
                set({ glgame: glgame || twoWayGames[0].gameId, game: "" })
              }
            >
              G League
            </button>
          </span>
        )}
        {w === "game" && (
          <select
            value={glgame || game || gameId || ""}
            onChange={(e) =>
              set(
                glgame ? { glgame: e.target.value } : { game: e.target.value },
              )
            }
            aria-label={glgame ? "G League game" : "Game"}
          >
            {(glgame ? twoWayGames : games.data?.games || []).map((g) => (
              <option key={g.gameId} value={g.gameId}>
                {g.label} · {g.result} {g.pts}–{g.oppPts}
                {g.seasonType === "preseason" ? " · pre" : ""}
              </option>
            ))}
          </select>
        )}
        {packet && (
          <>
            <span className="lbl">Report</span>
            <span className="kpi-seg">
              {PACKET_REPORTS.map((x) => (
                <button
                  key={x.key}
                  className={packetReport === x.key ? "on" : ""}
                  onClick={() => set({ report: x.key })}
                >
                  {x.label}
                </button>
              ))}
            </span>
          </>
        )}
        {!packet && <FullScreenButton target={root} />}
      </div>
      {/* Full screen hides the controls; this is the way back out (Esc works too). */}
      {!packet && <FullScreenButton target={root} floating />}
      {w === "custom" && (
        <GamePicker
          games={pickerGames}
          players={roster?.players}
          picked={picked}
          onChange={setPicked}
        />
      )}
      {customEmpty && (
        <Pending
          title="Custom"
          reason="Pick the games to include from the list above."
        />
      )}
      {load.error && (
        <Pending
          title="KPIs"
          reason={`Could not load the KPIs: ${load.error}`}
        />
      )}
      {data?.status?.state === "pending" && (
        <Pending title="KPIs" reason={data.status.reason} />
      )}
      {packet && query && (
        <PacketView
          query={query}
          w={w}
          winLabel={winLabel}
          win={win}
          goalsLabel={goalsLabel}
          variant={packetReport}
          picked={urlPicked}
        />
      )}
      {!packet && data?.status?.state === "ready" && data.meta.game && (
        <>
          {kind === "team" && (
            <TeamKpiPage
              data={data}
              windowed={w !== "game"}
              dashboard
              pageLabel={`Team KPIs · ${winLabel}`}
              goalsLabel={goalsLabel}
            />
          )}
          {kind === "players" && (
            <PlayerKpiPage
              data={data}
              onSettled={onSettled}
              windowLabel={w !== "game" ? winLabel : undefined}
              win={win}
              pageLabel={`Player KPIs · ${winLabel}`}
              goalsLabel={goalsLabel}
              grow
              hrefFor={(p) => playerLink(p.playerId)}
            />
          )}
          {kind === "player" && (
            <PlayerPage
              data={data}
              playerId={playerId}
              winHead={win}
              only={w === "game" ? (glgame ? "gleague" : "nba") : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}
